import { ClipboardCheck, Handshake, Network, Rocket, Shield, Target, Users } from "lucide-react";
import { LucideIcon } from "lucide-react";
import { DETAILED_CAPABILITY_GROUPS, flattenCapabilityGroups } from "./detailedCapabilities";

export const CDMO_STATUS = {
  CLAIMED: 'claimed',
  UNCLAIMED: 'unclaimed'
} as const;

export type CDMO_ENUM = typeof CDMO_STATUS[keyof typeof CDMO_STATUS];

/**
 * One optional facility a CDMO operates, held under a selected operating region.
 *
 * Every field except `region` is optional by design: a CDMO must be able to record equipment
 * without naming an exact location, in which case the sponsor card labels the record by region and
 * a generic facility number ("Europe — Facility 2").
 *
 * Stored as a flat array in `cdmo_applications.facilities` rather than nested under region keys —
 * grouping happens at render time, which keeps add/remove trivial and lets the sponsor card walk the
 * CDMO's selected regions in display order.
 */
export interface CDMOFacility {
  /** Stable client-side id so React keys and edits survive reordering. Not meaningful to the backend. */
  id: string;
  /** One of {@link OPERATING_REGIONS_OPTIONS}. Ties the record to a region the CDMO selected. */
  region: string;
  /** Free-text city / state / country. Blank is valid. */
  location: string;
  /**
   * Subset of the CDMO's own detailed capabilities available at this facility.
   *
   * Informational only — this never widens or narrows the CDMO's overall `detailed_capabilities`,
   * and matching continues to run off that profile-level list.
   */
  capabilities: string[];
  /** Free-text equipment, comma- or line-separated. Blank is valid. */
  equipment: string;
}

export interface FormData {
  // Company Information
  companyName: string;
  status: CDMO_ENUM;
  cdmoBusinessProfile: CDMOBusinessProfile;
  contactName: string;
  email: string;
  phone: string;
  website: string;
  feiNumbers?: string[]; // Optional FEI numbers for FDA lookups (up to 10)

  // Admin-created profile fields
  primaryContactEmail: string;
  additionalContactEmails: string[];
  adminNotes: string;

  // Primary Modalities (labelled "Primary Capabilities" before the Aug 2026 relabel)
  primaryCapabilities: string[];

  // Primary Capabilities (nested by modality; labelled "Detailed Capabilities" before the relabel)
  detailedCapabilities: Record<string, string[]>;

  // Client Fit Questions
  typicalBatchSizes: string[];
  targetClientTypes: string[];
  engagementScales: string[];
  preferredProjectTypes: string[];

  // Operational Filters
  gmpCertifications: string[];
  facilityLocationsDetails: string;
  /** Optional per-region facility records. See {@link CDMOFacility}. */
  facilities: CDMOFacility[];
  speedToStart: string;
  willingnessFeasibilityStudies: boolean;

  // Additional Information
  budgetRanges: string[];
  operatingRegions: string[];
  startupFriendly: boolean;
}

// Interface for pricing feature items
export interface PricingFeature {
  icon: LucideIcon;
  title: string;
  description: string;
}

// Interface for FAQ items
export interface FaqItem {
  question: string;
  answer: string;
}

// Data structures for form options
export const PRIMARY_CAPABILITIES_OPTIONS: string[] = [
  'Small Molecule',
  'Biologics',
  'Cell & Gene Therapy',
  'Vaccines',
  'Medical Devices'
];

/**
 * Display text for a primary-capability (modality) value. The stored/matched value stays exactly
 * what PRIMARY_CAPABILITIES_OPTIONS holds -- "Medical Devices" -- everywhere it's written to the
 * database, compared in gatekeeperFilters.ts, or used as a detailed_capabilities /
 * DETAILED_CAPABILITY_GROUPS key. Only what a person reads changes here.
 */
export const PRIMARY_CAPABILITY_DISPLAY_LABELS: Record<string, string> = {
  'Medical Devices': 'Medical Devices & Combination Products',
};

export function getPrimaryCapabilityLabel(value: string): string {
  return PRIMARY_CAPABILITY_DISPLAY_LABELS[value] ?? value;
}

export const BUDGET_RANGES_OPTIONS: string[] = [
  '< $50k',
  '$50k - $150k',
  '$150k - $300k',
  '$300k - $600k',
  '$600k - $1.5M',
  '$1.5M+'
];

export const validStages: string[] = [
  'Preclinical',
  'Phase I',
  'Phase II',
  'Phase III',
  'FDA Approval',
  'Commercial'
];

export const MAX_RESULTS = 5;

/** Field keys that can be locked by admin on CDMO intake/profile. Used for verified/lock toggles. */
export const CDMO_LOCKABLE_FIELD_KEYS = [
  'companyName',
  'contactName',
  'email',
  'phone',
  'website',
  'feiNumbers',
  'primaryCapabilities',
  'detailedCapabilities',
  'typicalBatchSizes',
  'budgetRanges',
  'targetClientTypes',
  'engagementScales',
  'preferredProjectTypes',
  'gmpCertifications',
  'operatingRegions',
  'facilityLocationsDetails',
  'facilities',
  'speedToStart',
  'willingnessFeasibilityStudies',
  'startupFriendly',
] as const;

export type CDMOLockableFieldKey = (typeof CDMO_LOCKABLE_FIELD_KEYS)[number];

export const CDMO_BUSINESS_PROFILE_OPTIONS = [
  'Boutique / Agile CDMO',
  'Mid-Size Specialist',
  'Large-Scale Global CDMO',
] as const;

export type CDMOBusinessProfile = (typeof CDMO_BUSINESS_PROFILE_OPTIONS)[number];

/**
 * Legacy matchmaking option list for “What type of partner are you looking for?”
 * (Tier 1 via `startup_friendly` / `target_client_types` keyword match).
 */
export const LEGACY_PARTNER_TYPE_SEARCH_OPTIONS: string[] = [
  'Startup-friendly and flexible',
  'Mid-size biotech specialist',
  'Large-scale pharma manufacturer',
  'Academic/government lab support',
  "Doesn't matter",
];

/** Matching sidebar: empty `cdmoPartnerType` filter means this (Tier 1 CDMO profile constraint off). */
export const CDMO_PARTNER_TYPE_SEARCH_FLEXIBLE_LABEL = 'Flexible / No Preference';

export const flexibleOptions: string[] = ['No preference', 'Not sure / flexible', 'Any', "Doesn't matter"];

export const GEO_MATCH_MESSAGES: Record<number, string> = {
  3: 'All selected regions supported',
  2: 'Some regions supported',
  1: 'No direct match, but supports common cross-region pairing',
  0: 'No geographic overlap',
};

export const OPERATING_REGIONS_OPTIONS: string[] = [
  'North America',
  'Europe',
  'Asia-Pacific',
  'Latin America',
  'Middle East & Africa'
];

export const CROSS_REGION_PAIRINGS: Record<string, string[]> = {
  'North America': ['Asia-Pacific', 'Latin America'],
  'Europe': ['Asia-Pacific', 'Latin America'],
  'Asia-Pacific': ['North America', 'Europe']
};

/**
 * Modality → flat list of detailed capability options.
 *
 * Derived from {@link DETAILED_CAPABILITY_GROUPS} rather than declared literally, so the grouped
 * picker UI and the matching engine can never disagree about which options exist. Edit the
 * taxonomy in `./detailedCapabilities`; this shape (and every consumer of it) stays unchanged.
 */
export const DETAILED_CAPABILITIES_OPTIONS_MAP: Record<string, string[]> = Object.fromEntries(
  Object.entries(DETAILED_CAPABILITY_GROUPS).map(([modality, groups]) => [
    modality,
    flattenCapabilityGroups(groups),
  ])
);

/**
 * Capability options for one modality, or an empty list when the modality is unknown.
 *
 * Prefer this to indexing {@link DETAILED_CAPABILITIES_OPTIONS_MAP} directly: modality keys come
 * from stored profiles, so a value like `'constructor'` would resolve up the prototype chain and
 * return the `Object` function instead of falling through to an empty array.
 */
export function getDetailedCapabilityOptions(modality: string): string[] {
  return Object.prototype.hasOwnProperty.call(DETAILED_CAPABILITIES_OPTIONS_MAP, modality)
    ? DETAILED_CAPABILITIES_OPTIONS_MAP[modality]
    : [];
}

/**
 * Selected state for an option tile on the CDMO and sponsor intakes.
 *
 * One constant because the client flagged the old `text-deep-indigo` as reading purple against the
 * tinted background: fixing that per call site left the approved blue on one question and the
 * flagged purple on the next. Every selected tile in a form shares this.
 */
export const SELECTED_TILE_CLASS = 'border-selected-blue bg-selected-blue/10 text-selected-blue';

export const BATCH_SIZES_KG_OPTIONS: string[] = [
  'Research Scale (<1kg)',
  'Pilot Scale (1-10kg)',
  'Clinical Scale (10-100kg)',
  'Small Commercial (100kg-1MT)',
  'Large Commercial (>1MT)'
];

export const BATCH_SIZES_L_OPTIONS: string[] = [
  'Research Scale (<1L)',
  'Pilot Scale (1-10L)',
  'Clinical Scale (10-100L)',
  'Small Commercial (100L-1000L)',
  'Large Commercial (>1000L)'
];

export const BATCH_SIZES_COMBINED_OPTIONS: string[] = [
  'Research Scale (<1kg / <1L)',
  'Pilot Scale (1-10kg / 1-10L)',
  'Clinical Scale (10-100kg / 10-100L)',
  'Small Commercial (100kg-1MT / 100L-1000L)',
  'Large Commercial (>1MT / >1000L)'
];

export const CLIENT_TYPES_OPTIONS: string[] = [
  'Early-stage Startups',
  'SME Biotech Companies',
  'Mid-size Pharma',
  'Large Pharma',
  'Academic Institutions',
  'Government/Non-profit'
];

/**
 * Answer shared by two distinct questions, declared once so the overlap is explicit rather than a
 * coincidence of two identical literals.
 *
 * As a **development stage** it lives in {@link ENGAGEMENT_SCALES_OPTIONS} → `engagement_scales`,
 * scored by the development-stage dimension and gating Tier 1. As a **support service** it lives in
 * {@link PROJECT_TYPES_OPTIONS} → `preferred_project_types`, scored by the support-services
 * dimension. Different columns, different dimensions — never conflate them.
 */
const COMMERCIAL_MANUFACTURING = 'Commercial Manufacturing';

export const ENGAGEMENT_SCALES_OPTIONS: string[] = [
  'Preclinical R&D',
  'Phase I Clinical',
  'Phase II Clinical',
  'Phase III Clinical',
  COMMERCIAL_MANUFACTURING,
  'Tech Transfer Support'
];

/**
 * Support-services options, shared by the CDMO intake (`preferred_project_types`) and the Sponsor
 * matching sidebar so the two sides always compare the same values.
 */
export const PROJECT_TYPES_OPTIONS: string[] = [
  'Feasibility Studies',
  'Process Development',
  'Method Development',
  'Tech Transfer',
  'Scale-up Support',
  'Regulatory Support',
  COMMERCIAL_MANUFACTURING
];

/**
 * Pre-rename support-service label → current label.
 *
 * The label is the stored value in `cdmo_applications.preferred_project_types`, and
 * `scoreSupportServices` compares it exactly, so a profile still holding "Full Manufacturing"
 * would score zero on the support dimension without this. A migration rewrites stored rows; this
 * is the safety net for anything it misses. Same role as
 * `LEGACY_DETAILED_CAPABILITY_ALIASES` and `LEGACY_CERTIFICATION_ALIASES`.
 */
export const LEGACY_SUPPORT_SERVICE_ALIASES: Record<string, string> = {
  'Full Manufacturing': COMMERCIAL_MANUFACTURING,
};

export const GMP_CERTIFICATIONS_OPTIONS: string[] = [
  'US FDA cGMP',
  'EU GMP',
  'ISO 13485, if device or combination-product related',
  'DEA Registration, if controlled substances apply',
  'Health Canada',
  'TGA / Australia',
  'PMDA / Japan',
  'Other regional requirements'
];

export const SPEED_TO_START_OPTIONS: string[] = [
  'Immediate (within 2 weeks)',
  'Fast (2-4 weeks)',
  'Standard (1-2 months)',
  'Extended (2-3 months)',
  'Project Dependent'
];

export const totalSteps: number = 6;

export const pricingFeatures: PricingFeature[] = [
  {
    icon: Rocket,
    title: 'Free for Biotech',
    description: 'Complete access to our CDMO network at no cost'
  },
  {
    icon: Shield,
    title: 'Quality Leads',
    description: 'CDMOs receive high-quality introduction requests'
  },
  {
    icon: Target,
    title: 'Algorithm Matching',
    description: 'Our matching algorithm ensures relevant connections'
  },
  {
    icon: Users,
    title: 'Warm Introductions',
    description: 'Direct, contextual introductions with project details'
  }
];

export const basicPlans: string[] = [
  'Receive warm introduction requests (very warm leads)',
  'Access detailed project requirements and company profiles',
  'Obtain direct contact information of interested prospects',
  'Request warm introductions',
  'View CDMO capabilities and certifications',
  'Project requirement matching',
  'Priority customer support'
];

export const premiumPlans: string[] = [
  'Receive warm introduction requests (very warm leads)',
  'Access detailed project requirements and company profiles',
  'Obtain direct contact information of interested prospects',
  'Accept/decline introduction requests',
  'Analytics on request volume and trends',
  'Early access to new platform features'
];

export const faqPageDetails: FaqItem[] = [
  {
    question: 'Why is it free for biotech companies?',
    answer: 'We believe in removing barriers for innovation. Biotech companies can focus on their science while we help them find manufacturing partners at no cost.'
  },
  {
    question: 'What do CDMOs pay for exactly?',
    answer: 'CDMOs pay for access to receive warm introduction requests from qualified biotech companies, including detailed project requirements and company information.'
  },
  {
    question: 'How does the matching work?',
    answer: 'Companies complete detailed profiles including development stage, project requirements, and timeline. Our matching algorithm connects them with relevant CDMOs based on capabilities and compatibility.'
  },
  {
    question: 'Can CDMOs cancel anytime?',
    answer: 'Yes, CDMOs can cancel their subscription at any time. They\'ll continue to receive introductions until the end of their billing period.'
  },
  {
    question: 'How many introduction requests do CDMOs receive?',
    answer: 'This varies based on your capabilities and market demand. Our matching algorithm ensures you only receive relevant requests that match your specialties.'
  },
  {
    question: 'Is there a setup fee for CDMOs?',
    answer: 'No setup fees. CDMOs pay a simple monthly subscription to access introduction requests and can start receiving leads immediately.'
  }
]

export const ForCDMOS = [
  {
    icon: ClipboardCheck,
    title: 'Apply to Become a Partner',
    description: 'Submit a comprehensive intake form describing your specialties, facility capabilities, capacity, and unique value propositions.',
    delay: 0.1
  },
  {
    icon: Network,
    title: 'Get Matched',
    description: 'Join our exclusive matching network where qualified startups are intelligently filtered to you based on mutual fit and compatibility.',
    delay: 0.2
  },
  {
    icon: Handshake,
    title: 'Connect & Collaborate',
    description: 'View warm introduction requests from qualified prospects on your dashboard. Subscribe to accept introductions and unlock direct warm leads.',
    delay: 0.3
  }
]

export const POLLING_TIME = 5000;

export const FDA_API_BASE_URL = 'https://api-datadashboard.fda.gov/v1/inspections_classifications';

export const FDA_API_HEADERS = {
  'Content-Type': 'application/json',
  'Authorization-User': "chetan.rajpurohit@growexx.com",
  'Authorization-Key': "hRkXYuK08b88gmfK"
};

export const MAX_INSPECTIONS = 50;
export const API_TIMEOUT = 10000;